import { useEffect, useState, useRef } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Phone, Mail, MessageCircle, Download, Share2, User } from "lucide-react";

function BusinessCard({ person }) {
  const [qr, setQr] = useState("");
  const canvasRef = useRef(null);

  const vcf = `BEGIN:VCARD\nVERSION:3.0\nFN:${person.name}\nTITLE:${person.title || ""}\nORG:Grupo Almar Investimentos\nTEL:${person.phone || ""}\nEMAIL:${person.email}\nEND:VCARD`;

  useEffect(() => {
    QRCode.toDataURL(vcf, { margin: 1, width: 200, color: { dark: "#0D1B2A", light: "#E0C574" } })
      .then(setQr).catch(() => {});
  }, [person.id]);

  const download = () => {
    const blob = new Blob([vcf], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${person.name.replace(/ /g, "_")}.vcf`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Contacto guardado (.vcf)");
  };

  const share = async () => {
    const text = `${person.name} — ${person.title}\nGrupo Almar Investimentos\n${person.phone}\n${person.email}`;
    if (navigator.share) { try { await navigator.share({ title: person.name, text }); } catch {} }
    else { navigator.clipboard.writeText(text); toast.success("Cartão copiado para a área de transferência"); }
  };

  const wa = (person.whatsapp || person.phone || "").replace(/[^0-9]/g, "");

  return (
    <div className="mx-auto w-full max-w-sm" data-testid="business-card">
      <div className="rounded-[2rem] p-1 shadow-2xl" style={{ background: "linear-gradient(145deg,#C9A84C,#9B7B2B)" }}>
        <div className="rounded-[1.85rem] bg-[#0D1B2A] overflow-hidden">
          <div className="h-28 relative" style={{ background: "linear-gradient(135deg,#152A3E,#0D1B2A)" }}>
            <div className="absolute inset-0 ambient-glow" />
            <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
              <img src={person.photo_url || "https://ui-avatars.com/api/?name=" + encodeURIComponent(person.name)}
                alt="" className="w-24 h-24 rounded-full object-cover ring-4 ring-[#0D1B2A] border border-[#C9A84C]/50" />
            </div>
          </div>
          <div className="pt-14 pb-6 px-6 text-center">
            <h3 className="font-display text-xl text-slate-100">{person.name}</h3>
            <p className="text-sm text-[#E0C574] mt-0.5">{person.title}</p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">Grupo Almar Investimentos</p>

            {qr && <img src={qr} alt="QR" className="w-32 h-32 mx-auto my-4 rounded-lg" />}

            <div className="space-y-2 text-left text-sm">
              <a href={`tel:${person.phone}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#152A3E] hover:bg-[#1A344D] transition-all text-slate-200"><Phone size={15} className="text-[#C9A84C]" /> {person.phone}</a>
              <a href={`mailto:${person.email}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#152A3E] hover:bg-[#1A344D] transition-all text-slate-200 break-all"><Mail size={15} className="text-[#C9A84C]" /> {person.email}</a>
              {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-2.5 rounded-lg bg-[#152A3E] hover:bg-[#1A344D] transition-all text-slate-200"><MessageCircle size={15} className="text-[#25D366]" /> WhatsApp</a>}
            </div>

            <div className="grid grid-cols-2 gap-2 mt-5">
              <button onClick={download} data-testid="vcf-download-btn" className="flex items-center justify-center gap-1.5 bg-[#C9A84C] hover:bg-[#D9B85C] text-[#0D1B2A] font-semibold py-2.5 rounded-lg transition-all text-sm"><Download size={15} /> Guardar</button>
              <button onClick={share} data-testid="share-card-btn" className="flex items-center justify-center gap-1.5 border border-[#C9A84C] text-[#E0C574] hover:bg-[#C9A84C]/10 py-2.5 rounded-lg transition-all text-sm"><Share2 size={15} /> Partilhar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CartaoDigital() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(user);

  useEffect(() => {
    if (user.role === "admin") api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [user.role]);

  return (
    <Layout title="Cartão de Visita Digital">
      {user.role === "admin" && (
        <div className="card-almar p-4 mb-6 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-slate-400 flex items-center gap-2"><User size={15} className="text-[#C9A84C]" /> Ver cartão de:</span>
          <select className="input-almar sm:w-72" data-testid="card-consultant-select"
            value={selected?.id} onChange={(e) => setSelected(users.find((u) => u.id === e.target.value) || user)}>
            <option value={user.id}>{user.name} (eu)</option>
            {users.filter((u) => u.id !== user.id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}
      <BusinessCard person={selected || user} />
    </Layout>
  );
}
